import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { exchangeCode, me } from '../services/authApi';
import Spinner from '../components/ui/Spinner';

// Backend redirects here as ${FRONTEND}/auth/google/callback?code=<one-time-code>
// after the Google OAuth dance. We POST the code to /auth/exchange for the app
// JWT + user, then immediately scrub the code from the URL/history so it never
// lingers, and land on '/'. (A bare ?token= is still tolerated for back-compat.)
export default function AuthGoogleCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const setUser = useAuthStore((s) => s.setUser);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    // Drop the sensitive query (code/token) from the address bar + history.
    const scrubUrl = () =>
      window.history.replaceState({}, '', '/auth/google/callback');

    const errParam = params.get('error');
    if (errParam) {
      setError(decodeURIComponent(errParam));
      scrubUrl();
      return;
    }

    const code = params.get('code');
    if (code) {
      exchangeCode(code)
        .then(({ token, user }) => {
          setAuth(token, user);
          scrubUrl();
          navigate('/', { replace: true });
        })
        .catch(() => {
          scrubUrl();
          setError('Could not complete Google sign-in. Please try again.');
        });
      return;
    }

    // Back-compat: legacy flow that put the JWT directly in the URL.
    const token = params.get('token');
    if (token) {
      setAuth(token, null);
      scrubUrl();
      me()
        .then((user) => {
          setUser(user);
          navigate('/', { replace: true });
        })
        .catch(() => navigate('/', { replace: true }));
      return;
    }

    setError('No authorization code returned from Google sign-in.');
    scrubUrl();
  }, [params, setAuth, setUser, navigate]);

  return (
    <div className="min-h-screen grid place-items-center bg-ws-bg">
      {error ? (
        <div className="text-center">
          <p className="text-sm text-ws-danger">{error}</p>
          <button className="mt-3 text-sm text-ws-accent underline" onClick={() => navigate('/login')}>
            Back to sign in
          </button>
        </div>
      ) : (
        <Spinner size={22} label="Completing sign-in…" />
      )}
    </div>
  );
}
